/**
 * byopStore — Sprint 28.17 PR-3a Settings + BYOP.
 *
 * Holds the user's BYOP (Bring Your Own Pollen) Pollinations API key
 * after they connect through the OAuth-style redirect flow. Lets them
 * use their own pollen quota for image / video / audio gen instead of
 * the operator's sk_ proxy.
 *
 * Storage choice: **sessionStorage**, not localStorage. The trade-off:
 *
 *  - sessionStorage: BYOP key disappears on tab close. User reconnects
 *    on the next session. Higher friction; lower XSS exposure window.
 *
 *  - localStorage: Key persists across tabs / restarts (matches
 *    Pollinations' default 30-day key expiry). Lower friction; longer
 *    exposure window.
 *
 * For v1 we choose sessionStorage — the BYOP feature is opt-in and
 * power-user-flavoured, the security cost of an XSS leaking a key with
 * Pollen budget is real, and "reconnect once per browser session" is a
 * reasonable trade. Add a "Remember on this device" toggle later if
 * power users complain.
 *
 * Mutations:
 *   connect(apiKey, scope)  → store key + timestamp; refuses non-sk_ shapes
 *   disconnect()            → wipe everything
 *
 * Selectors:
 *   apiKey: string | null   — used by image/video/audio resolvers
 *   hasConnection(): boolean — used by Settings UI + contextual nudges
 *
 * Cross-store coupling: zero. Resolvers read `useByopStore.getState()`
 * imperatively from inside async handlers — same pattern as
 * useUserStore.effectiveTier() in Imagine.tsx.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { isValidSecretKey } from '../lib/byop.ts';

const STORE_KEY = 'jubuddy:byop-store:v1';

interface ByopState {
  /** User's connected sk_ key, or null when disconnected. */
  apiKey: string | null;
  /** ms epoch when they connected. Null when disconnected. */
  connectedAt: number | null;
  /** Scope they granted on the consent screen, e.g. 'usage'. */
  scope: string | null;
  /** Connect a new key. Refuses anything that isn't sk_-prefixed. */
  connect: (apiKey: string, scope?: string) => void;
  /** Wipe the connection. */
  disconnect: () => void;
  /** Cheap selector for UI / resolver — has the user connected? */
  hasConnection: () => boolean;
}

export const useByopStore = create<ByopState>()(
  persist(
    (set, get) => ({
      apiKey: null,
      connectedAt: null,
      scope: null,
      connect: (apiKey, scope = 'usage') => {
        // Defence in depth — refuse anything that isn't an sk_ key. A
        // pk_ in this slot would be a Pollinations bug, but we don't
        // want it landing in sessionStorage and being used for Bearer
        // auth (it would just 401 anyway).
        if (!isValidSecretKey(apiKey)) return;
        set({ apiKey, connectedAt: Date.now(), scope });
      },
      disconnect: () => {
        set({ apiKey: null, connectedAt: null, scope: null });
      },
      hasConnection: () => get().apiKey !== null,
    }),
    {
      name: STORE_KEY,
      // sessionStorage instead of localStorage default — see file header.
      storage: createJSONStorage(() => sessionStorage),
      partialize: (s) => ({
        apiKey: s.apiKey,
        connectedAt: s.connectedAt,
        scope: s.scope,
      }),
    },
  ),
);

/** Test-only reset — clears in-memory + persisted slice. */
export function __resetByopStoreForTests(): void {
  useByopStore.setState({ apiKey: null, connectedAt: null, scope: null });
  if (typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.removeItem(STORE_KEY);
    } catch {
      // Best effort — private mode / disabled storage shouldn't crash tests.
    }
  }
}
